import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Table, 
  Button, 
  InputGroup, 
  Spinner,
  Card,
  Badge,
  Pagination
} from 'react-bootstrap';
import { FaSearch, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import Select from 'react-select';

import TransportService from '../services/TransportService';
import { formatCurrency } from '../utils/formatters';

const TransportTarix = ({ transportId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [transports, setTransports] = useState([]);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [transportOptions, setTransportOptions] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Get first day of current month in YYYY-MM-DD format
  const getFirstDayOfMonth = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`; // First day of current month
  };

  // Get tomorrow's date in YYYY-MM-DD format
  const getToday = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1); // Add one day to get tomorrow
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // Tomorrow's date
  };

  const [filters, setFilters] = useState(() => ({
    dateDan: getFirstDayOfMonth(),
    dateGacha: getToday(),
    page: 1,
    perPage: 10
  }));

  // Available per page options
  const perPageOptions = [10, 20, 50, 100];

  // Load transports on component mount
  useEffect(() => {
    const loadTransports = async () => {
      try {
        const response = await TransportService.getTransportList();
        if (response && response.data) {
          const options = response.data.map(transport => ({
            value: transport.id,
            label: `${transport.name || ''} ${transport.davlat_raqam ? `(${transport.davlat_raqam})` : ''}`.trim(),
            transportData: transport
          }));
          setTransportOptions(options);
          
          // If transportId is provided, select that transport
          if (transportId) {
            const transportToSelect = options.find(opt => opt.value === transportId);
            if (transportToSelect) {
              setSelectedTransport(transportToSelect);
            } else if (options.length > 0) {
              // If specified transport not found, select first one
              setSelectedTransport(options[0]);
            }
          } else if (options.length > 0) {
            // If no transportId provided, select first transport
            setSelectedTransport(options[0]);
          }
          
          setIsInitialLoad(false);
        }
      } catch (error) {
        console.error('Error loading transports:', error);
        toast.error('Transportlar yuklanmadi');
        setIsInitialLoad(false);
      }
    };

    loadTransports();
  }, [transportId]); // Add transportId to dependency array

  // Load data when filters or selected transport changes
  useEffect(() => {
    if (selectedTransport && !isInitialLoad) {
      loadData();
    }
  }, [selectedTransport, filters, isInitialLoad]);

  const loadData = async () => {
    if (!selectedTransport) return;
    
    try {
      setLoading(true);
      
      const response = await TransportService.transportHistoryShow(
        selectedTransport.value,
        filters.page,
        filters.perPage,
        filters.dateDan,
        filters.dateGacha
      );

      if (response && response.data) {
        setTransactions(response.data.data || []);
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.last_page || 1);
      }
    } catch (error) {
      console.error('Error loading transport history:', error);
      toast.error('Tarix yuklanmadi');
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page: page
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page when filters change
    }));
  };
  
  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setFilters(prev => ({
      ...prev,
      perPage: newPerPage,
      page: 1 // Reset to first page when changing items per page
    }));
  };

  const handleTransportChange = (selectedOption) => {
    setSelectedTransport(selectedOption);
    setFilters(prev => ({
      ...prev,
      page: 1
    }));
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Format date to display with time
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm:ss', { locale: ru });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Helper to parse user string (format: "name phone")
  const parseUserInfo = (userString) => {
    if (!userString) return { name: '-', phone: '' };
    const [name, ...phoneParts] = userString.split(' ');
    return {
      name: name || '-',
      phone: phoneParts.join(' ') || ''
    };
  };

  return (
    <Container fluid className="py-4">
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
          <h5 className="mb-0">Transport harakati tarixi</h5>
          <div style={{ minWidth: '300px' }}>
            <Select
              value={selectedTransport}
              onChange={handleTransportChange}
              options={transportOptions}
              placeholder="Transportni tanlang..."
              isSearchable
              isDisabled={transportOptions.length === 0}
              isLoading={isInitialLoad}
              loadingMessage={() => "Yuklanmoqda..."}
              noOptionsMessage={() => "Transport topilmadi"}
              styles={{
                control: (provided) => ({
                  ...provided,
                  border: '1px solid #0d6efd',
                  boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
                  '&:hover': {
                    borderColor: '#0d6efd',
                  },
                }),
              }}
            />
          </div>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <Form onSubmit={handleSearch} className="mb-4">
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Sanadan</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateDan"
                    value={filters.dateDan}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Sanagacha</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateGacha"
                    value={filters.dateGacha}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>

          {/* Transactions Table */}
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Sana</th>
                  <th>Amaliyot</th>
                  <th>Summa</th>
                  <th>Balans</th>
                  <th>Izoh</th>
                  <th>F.I.O</th>
                </tr>
              </thead>
              <tbody>
                {!selectedTransport ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      Iltimos, transportni tanlang
                    </td>
                  </tr>
                ) : loading ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      Ma'lumot topilmadi
                    </td>
                  </tr>
                ) : (
                  transactions.map((item, index) => (
                    <tr key={`${item.id}-${item.kassa_kirim_id || item.kassa_chiqim_id || item.putyofka_id || item.litsenziya_id}`}>
                      <td>{(filters.page - 1) * filters.perPage + index + 1}</td>
                      <td>{formatDate(item.created_at)}</td>
                      <td>
                        {item.kassa_kirim_id ? (
                          <Badge bg="success">Kirim</Badge>
                        ) : item.kassa_chiqim_id ? (
                          <Badge bg="danger">Chiqim</Badge>
                        ) : item.putyofka_id ? (
                          <Badge bg="info">Putyovka</Badge>
                        ) : item.litsenziya_id ? (
                          <Badge bg="warning" text="dark">Litsenziya</Badge>
                        ) : (
                          <Badge bg="secondary">Noma'lum</Badge>
                        )}
                      </td>
                      <td className={item.summa >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                        {item.summa >= 0 ? '+ ' : '- '}{formatCurrency(Math.abs(item.summa))}
                      </td>
                      <td className="fw-bold">
                        {formatCurrency(item.balance)}
                      </td>
                      <td>{item.izoh || '-'}</td>
                      <td>
                        {item.user ? (
                          <>
                            {parseUserInfo(item.user).name}
                            {parseUserInfo(item.user).phone && ` (${parseUserInfo(item.user).phone})`}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {/*{totalPages > 1 && (*/}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center">
                <span className="me-2">Sahifadagi qatorlar soni:</span>
                <Form.Select 
                  value={filters.perPage} 
                  onChange={handlePerPageChange}
                  style={{ width: '80px' }}
                  disabled={loading}
                >
                  {perPageOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
                <span className="ms-2">Jami: {totalItems}</span>
              </div>
              
              <Pagination className="mb-0">
                <Pagination.First 
                  onClick={() => handlePageChange(1)} 
                  disabled={filters.page === 1 || loading}
                />
                <Pagination.Prev 
                  onClick={() => handlePageChange(filters.page - 1)} 
                  disabled={filters.page === 1 || loading}
                />
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (filters.page <= 3) {
                    pageNum = i + 1;
                  } else if (filters.page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = filters.page - 2 + i;
                  }
                  
                  return (
                    <Pagination.Item 
                      key={pageNum}
                      active={pageNum === filters.page}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next 
                  onClick={() => handlePageChange(filters.page + 1)} 
                  disabled={filters.page === totalPages || loading}
                />
                <Pagination.Last 
                  onClick={() => handlePageChange(totalPages)} 
                  disabled={filters.page === totalPages || loading}
                />
              </Pagination>
            </div>
          {/*)}*/}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TransportTarix;
